import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { ArrowRight, CheckCircle2, Clock, CreditCard, PackageCheck } from "lucide-react";
import { api, fmtEUR, fmtXOF } from "../lib/api";
import { useCart } from "../context/CartContext";
import { Reveal } from "../components/Reveal";
import { sizeLabel } from "../lib/sizeLabel";

export default function Confirmation() {
  const [params] = useSearchParams();
  const ref = params.get("ref");
  const { t } = useTranslation();
  const [order, setOrder] = useState(null);
  const [error, setError] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const { clear } = useCart();
  const clearedRef = useRef(false);

  const load = () => api.get(`/orders/${ref}`).then((r) => setOrder(r.data)).catch(() => setError(true));

  useEffect(() => {
    if (!ref) { setError(true); return; }
    load();
    const t = setInterval(() => {
      api.get(`/orders/${ref}`).then((r) => {
        setOrder(r.data);
        if (r.data.payment_status === "payee") clearInterval(t);
      }).catch(() => {});
    }, 5000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ref]);

  useEffect(() => {
    if (order && !clearedRef.current) {
      clearedRef.current = true;
      clear();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order?.ref]);

  const confirmPayment = async () => {
    setConfirming(true);
    try {
      const r = await api.post(`/orders/${ref}/pay`);
      setOrder(r.data);
      if (r.data.payment_status === "payee") toast.success(t("confirmation.payOk"));
      else toast.info(t("confirmation.payPending"));
    } catch {
      toast.error(t("confirmation.payErr"));
    } finally {
      setConfirming(false);
    }
  };

  if (error)
    return (
      <main className="flex min-h-screen flex-col items-center justify-center gap-6 px-6 text-center">
        <p className="font-serif text-3xl text-ivoire">{t("confirmation.notFound")}</p>
        <Link data-testid="confirmation-notfound-back" to="/boutique" className="border border-or/60 px-8 py-4 font-mono text-[11px] uppercase tracking-[0.3em] text-or hover:bg-or hover:text-noir">
          {t("common.backToShop")}
        </Link>
      </main>
    );

  if (!order)
    return <main className="min-h-screen pt-40 text-center font-mono text-[11px] uppercase tracking-[0.3em] text-argile">{t("common.loading")}</main>;

  const paid = order.payment_status === "payee";
  // En mode simulation, le texte stocké est en français : on l'affiche traduit.
  const instructions = order.payment_mode === "simulation" ? t("confirmation.momoSim") : order.payment_instructions;

  return (
    <main data-testid="confirmation-page" className="mx-auto max-w-3xl px-6 pb-24 pt-36 lg:pt-44">
      <Reveal>
        <div className="text-center">
          {paid ? (
            <CheckCircle2 data-testid="order-status-paid-icon" size={52} strokeWidth={1} className="mx-auto text-or" />
          ) : (
            <Clock data-testid="order-status-pending-icon" size={52} strokeWidth={1} className="mx-auto text-or" />
          )}
          <h1 className="mt-6 font-serif text-4xl uppercase tracking-tight text-ivoire sm:text-5xl">
            {paid ? t("confirmation.paidTitle") : t("confirmation.pendingTitle")}
          </h1>
          <p className="mt-4 text-sm text-argile">
            {t("confirmation.ref")}{' '}
            <span data-testid="order-ref" className="font-mono tracking-[0.2em] text-or">{order.ref}</span>
          </p>
          {order.payment_mode === "simulation" && (
            <p data-testid="order-simulation-badge" className="mx-auto mt-5 inline-block border border-or/40 px-4 py-2 font-mono text-[9px] uppercase tracking-[0.2em] text-or">
              {t("confirmation.simBadge")}
            </p>
          )}
        </div>
      </Reveal>

      {order.payment_method === "carte" && !paid && (
        <Reveal delay={0.1}>
          <div className="mt-10 flex items-center justify-center gap-3 border border-ligne bg-card2 p-6 text-center">
            <CreditCard size={16} className="text-or" />
            <p className="text-sm text-argile">{t("confirmation.cardPending")}</p>
          </div>
        </Reveal>
      )}

      {order.payment_method === "mobile_money" && !paid && (
        <Reveal delay={0.1}>
          <div className="mt-10 border border-ligne bg-card2 p-6 text-center">
            {instructions && <p className="text-sm leading-relaxed text-argile">{instructions}</p>}
            <a
              data-testid="nicepay-pay-link-button"
              href={`${process.env.REACT_APP_BACKEND_URL}/api/payments/nicepay/redirect?ref=${order.ref}`}
              className="mt-6 flex w-full items-center justify-center gap-3 bg-or py-4 font-mono text-[11px] uppercase tracking-[0.3em] text-noir transition-all hover:bg-orclair"
            >
              {t("confirmation.payNow")} <ArrowRight size={14} className="rtl:rotate-180" />
            </a>
            <button
              data-testid="confirm-payment-button"
              onClick={confirmPayment}
              disabled={confirming}
              className="mt-5 border border-or/60 px-8 py-3.5 font-mono text-[10px] uppercase tracking-[0.25em] text-or transition-all hover:bg-or hover:text-noir disabled:opacity-50"
            >
              {confirming ? t("confirmation.verifying") : t("confirmation.validated")}
            </button>
          </div>
        </Reveal>
      )}

      <Reveal delay={0.15}>
        <div className="mt-12 border border-ligne bg-card2 p-8">
          <ul className="space-y-5" data-testid="confirmation-items">
            {order.items.map((i, idx) => (
              <li key={idx} className="flex items-center gap-4">
                <img src={i.image} alt={i.name} className="h-20 w-16 object-cover" />
                <div className="flex-1">
                  <p className="font-serif text-base text-ivoire">{i.name}</p>
                  <p className="mt-1 font-mono text-[9px] uppercase tracking-[0.2em] text-argile">{t("common.sizeX", { size: sizeLabel(i.size, t), n: i.quantity })}</p>
                </div>
                <p className="text-sm text-or">{fmtXOF(i.price_xof * i.quantity)}</p>
              </li>
            ))}
          </ul>
          <div className="mt-8 space-y-3 border-t border-ligne pt-6 text-sm">
            <div className="flex justify-between text-argile"><span>{t("common.subtotal")}</span><span>{fmtXOF(order.subtotal_xof)}</span></div>
            <div className="flex justify-between text-argile"><span>{t("common.delivery")}</span><span>{order.delivery_xof === 0 ? t("common.free") : fmtXOF(order.delivery_xof)}</span></div>
            <div className="flex items-baseline justify-between border-t border-ligne pt-4">
              <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-ivoire">{t("common.total")}</span>
              <div className="text-end" data-testid="confirmation-total">
                <p className="font-serif text-2xl text-or">{fmtXOF(order.total_xof)}</p>
                <p className="text-xs text-argile">{fmtEUR(order.total_xof)}</p>
              </div>
            </div>
          </div>
        </div>
      </Reveal>

      <Reveal delay={0.2}>
        <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
          {paid && (
            <p data-testid="confirmation-delivery-note" className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-argile">
              <PackageCheck size={14} className="text-or" /> {t("confirmation.deliveryNote")}
            </p>
          )}
          <Link data-testid="confirmation-back-boutique" to="/boutique" className="border border-ligne px-8 py-4 font-mono text-[11px] uppercase tracking-[0.3em] text-ivoire transition-all hover:border-or hover:text-or">
            {t("confirmation.continue")}
          </Link>
        </div>
      </Reveal>
    </main>
  );
}
